import {
  BROWSER_CONTEXT_COOKIE_NAME,
  type BrowserContext,
} from "@quotientjs/core"
import { SESSION_TIMEOUT_MS } from "./constants"

/**
 * Writes the current `BrowserContext` to the `qt_browser_context` cookie so
 * servers on the same site can read what the SDK knows about the visitor. Local
 * storage stays the source of truth; the cookie is a copy that
 * `BrowserContextManager` rewrites whenever the context changes.
 */
export class BrowserContextCookie {
  write(context: BrowserContext): void {
    // The cookie lives exactly as long as the session would without further
    // activity, and every tracked event rewrites it, so a server never reads
    // attribution the SDK itself has already discarded.
    const maxAgeSeconds = Math.floor(SESSION_TIMEOUT_MS / 1_000)
    const value = encodeURIComponent(JSON.stringify(context))
    this.set(
      `${BROWSER_CONTEXT_COOKIE_NAME}=${value}; Max-Age=${maxAgeSeconds}`,
    )
  }

  clear(): void {
    this.set(`${BROWSER_CONTEXT_COOKIE_NAME}=; Max-Age=0`)
  }

  private set(nameValueAndAge: string): void {
    if (typeof document === "undefined") {
      return
    }
    const secure =
      typeof window !== "undefined" && window.location.protocol === "https:"
        ? "; Secure"
        : ""
    // Cookie access can throw in sandboxed frames. The cookie is a best-effort
    // mirror, so a failed write must not break event tracking.
    try {
      document.cookie = `${nameValueAndAge}; Path=/; SameSite=Lax${secure}`
    } catch {
      // ignore: the next tracked event tries again
    }
  }
}
